<template>
	<button
		v-if="store.draft && collapsed"
		type="button"
		class="reopen-button"
		:style="{ top: reopenTop, right: `calc(${reopenRight} + var(--spacing-md))` }"
		:title="t('comparableAnalysis.expand')"
		@click="collapsed = false"
	>
		<AppIcon :name="IconName.BarChart" :size="16" />
		<span>{{ panelTitle }}</span>
		<span class="count">{{ store.draft.input.comparableInscriptionNumbers.length }}</span>
	</button>
	<aside v-else-if="store.draft" class="analysis-panel" :aria-label="panelTitle">
		<header>
			<h4>
				{{ panelTitle }}
				<span class="count">({{ store.draft.input.comparableInscriptionNumbers.length }}/50)</span>
			</h4>
			<div class="panel-actions">
				<button
					type="button"
					class="header-button"
					:aria-label="t('comparableAnalysis.collapse')"
					:title="t('comparableAnalysis.collapse')"
					@click="collapsed = true"
				>
					<AppIcon :name="IconName.PanelRightCollapse" :size="18" :stroke-width="1.2" />
				</button>
				<button
					type="button"
					class="header-button"
					:disabled="store.saving"
					:aria-label="t(store.draft.target ? 'comparableAnalysis.returnToAnalysis' : 'comparableAnalysis.cancel')"
					:title="t(store.draft.target ? 'comparableAnalysis.returnToAnalysis' : 'comparableAnalysis.cancel')"
					@click="close"
				>
					<AppIcon :name="IconName.CloseSmall" :size="18" :stroke-width="1.2" />
				</button>
			</div>
		</header>
		<div class="body">
			<p v-if="store.draft.target" class="help">{{ t("comparableAnalysis.mapSelectionHelp") }}</p>
			<label v-if="!store.draft.target" class="field"
				>{{ t("comparableAnalysis.name")
				}}<input v-model="store.draft.input.name" maxlength="200" :disabled="store.saving" />
			</label>
			<section>
				<h5>{{ t("comparableAnalysis.subject") }}</h5>
				<ul class="parcels">
					<li v-for="parcel in subjectParcels" :key="parcel">{{ parcel }}</li>
				</ul>
			</section>
			<section>
				<h5>{{ t("comparableAnalysis.comparables") }}</h5>
				<p v-if="!store.draft.input.comparableInscriptionNumbers.length" class="help">
					{{ t("comparableAnalysis.selectHelp") }}
				</p>
				<ol v-else>
					<li v-for="(number, index) in store.draft.input.comparableInscriptionNumbers" :key="number">
						<span class="position">#{{ index + 1 }}</span>
						<div class="transaction">
							<span class="number">{{ formatInscriptionNumber(number) }}</span>
							<template v-if="store.draft.summaries?.[number]">
								<span v-if="store.draft.summaries[number].address" class="address">{{
									store.draft.summaries[number].address
								}}</span>
								<span class="details">{{
									formatComparableTransactionDetails(store.draft.summaries[number], locale).join(" · ")
								}}</span>
							</template>
						</div>
						<button
							v-if="!store.isExistingComparable(number)"
							type="button"
							class="icon-button remove"
							:disabled="store.saving"
							:aria-label="`${t('comparableAnalysis.remove')} ${formatInscriptionNumber(number)}`"
							:title="t('comparableAnalysis.remove')"
							@click="store.removeTransaction(number)"
						>
							<AppIcon :name="IconName.CloseSmall" :size="12" :stroke-width="1.5" />
						</button>
					</li>
				</ol>
			</section>
		</div>
		<footer>
			<p v-if="error" class="banner" role="alert">
				<AppIcon :name="IconName.CircleAlert" :size="16" />
				<span>{{ error }}</span>
			</p>
			<Button
				class="save"
				variant="primary"
				size="xs"
				shape="pill"
				:disabled="
					store.saving ||
					(!store.draft.target &&
						(!store.draft.input.name.trim() || !store.draft.input.comparableInscriptionNumbers.length))
				"
				@click="store.draft.target ? returnToAnalysis() : save()"
				>{{
					t(
						store.draft.target
							? "comparableAnalysis.returnToAnalysis"
							: store.saving
								? "comparableAnalysis.saving"
								: "comparableAnalysis.saveAndOpen"
					)
				}}</Button
			>
		</footer>
	</aside>
</template>
<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from "vue"
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRouter } from "vue-router"
import { useI18n } from "vue-i18n"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import Button from "@/components/ui/Button.vue"
import { useComparableAnalysisStore } from "@/stores/comparableAnalysisStore"
import { formatInscriptionNumber } from "@/utils/format"
import { formatComparableTransactionDetails } from "@/utils/comparableTransactionSummary"
import { Route } from "@/utils/routing"
const store = useComparableAnalysisStore()
const router = useRouter()
const { t, locale } = useI18n()
withDefaults(defineProps<{ reopenRight?: string; reopenTop?: string }>(), {
	reopenRight: "0px",
	reopenTop: "var(--spacing-md)",
})
const collapsed = defineModel<boolean>("collapsed", { default: false })
const error = ref("")
const panelTitle = computed(() => (store.draft?.target ? store.draft.input.name : t("comparableAnalysis.preparation")))
const subjectParcels = computed(() =>
	store.draft?.subjectParcelsText !== undefined
		? store.draft.subjectParcelsText
				.split(",")
				.map(value => value.trim())
				.filter(Boolean)
		: store.draft?.input.subjectParcelIds ?? []
)
async function returnToAnalysis() {
	if (store.draft?.target && !store.saving)
		await router.push({ name: Route.ComparableAnalysis, params: { id: store.draft.target.id } })
}
function close() {
	if (store.draft?.target) void returnToAnalysis()
	else store.cancel()
}
watch(
	() => store.draft,
	draft => {
		if (!draft) collapsed.value = false
	}
)
let contextRevision = 0
function invalidateContext() {
	contextRevision++
}
onBeforeRouteLeave(invalidateContext)
onBeforeRouteUpdate(invalidateContext)
onUnmounted(invalidateContext)
async function save() {
	const request = contextRevision
	error.value = ""
	try {
		const saved = await store.saveDraft()
		if (saved && request === contextRevision && store.canWrite)
			await router.push({ name: Route.ComparableAnalysis, params: { id: saved.id } })
	} catch (failure) {
		if (request === contextRevision)
			error.value = failure instanceof Error ? failure.message : t("comparableAnalysis.error")
	}
}
</script>
<style scoped>
.analysis-panel {
	position: absolute;
	top: 0;
	right: 0;
	bottom: 0;
	z-index: 20;
	width: var(--width-detail-panel);
	max-width: 100vw;
	box-sizing: border-box;
	background: var(--neutral-0);
	box-shadow: var(--shadow-xl);
	display: flex;
	flex-direction: column;
	font-family: var(--font-family-primary);
	font-size: var(--font-size-small);
	color: var(--neutral-900);
	header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: var(--spacing-sm);
		padding: var(--spacing-sm) var(--spacing-sm) var(--spacing-sm) var(--spacing-md);
		border-bottom: var(--border-width-thin) solid var(--neutral-200);
		h4 {
			margin: 0;
			font-size: var(--font-size-base);
			font-weight: var(--font-weight-medium);
			.count {
				color: var(--neutral-600);
				font-weight: var(--font-weight-normal);
			}
		}
		.panel-actions {
			display: flex;
			gap: var(--spacing-xs);
		}
	}
	.header-button {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: var(--spacing-sm);
		border: none;
		border-radius: var(--border-radius-sm);
		background: none;
		color: var(--neutral-600);
		cursor: pointer;
		&:hover:not(:disabled) {
			background: var(--neutral-100);
			color: var(--neutral-900);
		}
		&:disabled {
			cursor: not-allowed;
			opacity: 0.5;
		}
	}
	.icon-button {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
		width: var(--space-24);
		height: var(--space-24);
		padding: 0;
		border: 0;
		border-radius: var(--border-radius-sm);
		background: transparent;
		color: var(--neutral-600);
		cursor: pointer;
		&:hover:not(:disabled) {
			background: var(--neutral-100);
			color: var(--neutral-900);
		}
		&:disabled {
			cursor: not-allowed;
			opacity: 0.5;
		}
		&.remove:hover:not(:disabled) {
			color: var(--accent-warning);
		}
	}
	.body {
		flex: 1;
		min-height: 0;
		overflow-y: auto;
		display: flex;
		flex-direction: column;
		gap: var(--spacing-lg);
		padding: var(--spacing-md);
	}
	.field {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs);
		font-size: var(--font-size-xs);
		color: var(--neutral-600);
	}
	input {
		height: var(--size-button-s);
		box-sizing: border-box;
		padding: 0 var(--spacing-sm);
		border: var(--border-width-thin) solid var(--neutral-400);
		border-radius: var(--border-radius-base);
		background: var(--neutral-0);
		font: inherit;
		font-size: var(--font-size-small);
		color: var(--neutral-900);
		outline: none;
		&:focus {
			border-color: var(--neutral-900);
		}
	}
	section {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);
	}
	h5 {
		margin: 0;
		font-size: var(--font-size-xs);
		font-weight: var(--font-weight-semibold);
		letter-spacing: 0.02em;
		text-transform: uppercase;
		color: var(--neutral-600);
	}
	.help {
		margin: 0;
		color: var(--neutral-600);
	}
	ul,
	ol {
		margin: 0;
		padding: 0;
		list-style: none;
	}
	.parcels {
		display: flex;
		flex-wrap: wrap;
		gap: var(--spacing-xs);
		li {
			padding: var(--spacing-xxs) var(--spacing-sm);
			border-radius: var(--border-radius-full);
			background: var(--accent-highlight-light);
			color: var(--primary-green);
			font-weight: var(--font-weight-medium);
		}
	}
	ol li {
		display: flex;
		align-items: flex-start;
		gap: var(--spacing-sm);
		padding: var(--spacing-sm) 0;
		border-bottom: var(--border-width-thin) solid var(--neutral-100);
		.position {
			padding: 0 var(--space-6);
			border-radius: var(--border-radius-full);
			background: var(--neutral-200);
			font-size: var(--font-size-xs);
			color: var(--neutral-700);
		}
		.transaction {
			display: flex;
			flex: 1;
			flex-direction: column;
			gap: var(--space-2);
			min-width: 0;
		}
		.number {
			font-weight: var(--font-weight-semibold);
		}
		.address,
		.details {
			overflow: hidden;
			white-space: nowrap;
			text-overflow: ellipsis;
		}
		.details {
			font-size: var(--font-size-xs);
			color: var(--neutral-600);
		}
	}
	footer {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);
		padding: var(--spacing-md);
		border-top: var(--border-width-thin) solid var(--neutral-200);
		.save {
			width: 100%;
		}
	}
	.banner {
		display: flex;
		align-items: center;
		gap: var(--spacing-sm);
		margin: 0;
		padding: var(--spacing-sm) var(--spacing-md);
		border-radius: var(--border-radius-md);
		background: color-mix(in srgb, var(--accent-warning) 10%, transparent);
		color: var(--accent-warning);
	}
	@media (max-width: 768px) {
		top: auto;
		bottom: 0;
		max-height: 45vh;
	}
}
.reopen-button {
	position: fixed;
	z-index: 5;
	display: flex;
	align-items: center;
	gap: var(--spacing-xs);
	height: var(--space-40);
	padding: 0 var(--spacing-md);
	border: none;
	border-radius: var(--border-radius-full);
	background: var(--neutral-0);
	box-shadow: var(--shadow-lg);
	color: var(--neutral-900);
	font-family: var(--font-family-primary);
	font-size: var(--font-size-small);
	font-weight: var(--font-weight-medium);
	cursor: pointer;
	transition:
		right 0.2s ease,
		box-shadow 0.15s ease;
	&:hover {
		box-shadow: var(--shadow-xl);
	}
	.count {
		display: flex;
		align-items: center;
		justify-content: center;
		min-width: var(--size-icon-lg);
		height: var(--size-icon-lg);
		padding: 0 var(--spacing-xxs);
		border-radius: var(--border-radius-full);
		background-color: var(--primary-green);
		color: var(--neutral-0);
		font-size: var(--font-size-xxs);
		font-weight: var(--font-weight-semibold);
	}
}
</style>
